import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useAuth } from "../../hooks/useAuth";
import "./site.css";
import { ChatWidget } from "./ChatWidget";

export type PublicConfig = { signup_enabled: boolean; currency: string; monthly_price: number; trial_days: number; support_email: string | null; contact_form?: boolean; founding_price?: number | null; founding_spots_left?: number | null; founding_spots_total?: number; operator_name?: string | null; workspace_joined: boolean; deployment_mode: "saas" | "self_hosted"; source_url?: string };
export function publicPrice(amount: number, currency = "USD") {
  return `${currency === "USD" ? "US$" : `${currency} `}${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
}
export function usePublicConfig() {
  return useQuery({
    queryKey: ["public-config"],
    queryFn: async (): Promise<PublicConfig> => {
      const response = await fetch("/api/public/config", { credentials: "same-origin" });
      if (!response.ok) throw new Error("Account availability could not be checked.");
      return response.json();
    },
    staleTime: 60_000,
    retry: 1,
  });
}

export function Brand() {
  return <Link to="/site" className="site-brand" aria-label="Trim Log home"><svg viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect width="32" height="32" rx="9" fill="currentColor"/><path d="M9 10h14M16 10v13M10 18l4 5 9-11" stroke="white" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"/></svg><span>trim log<span className="brand-period">.</span></span></Link>;
}

export function SourceLink({ children = "Source code", className }: { children?: ReactNode; className?: string }) {
  const { data } = usePublicConfig();
  const source = data?.source_url;
  const localArchive = source?.startsWith("/") && !source.startsWith("//") && !source.includes("\\");
  return <a className={className} href={source?.startsWith("https://") || localArchive ? source : "https://github.com/JakeTheRabbit/trimlog"}>{children}</a>;
}

/** `secondary`: drawn as an outline when a Buy now button sits beside it. */
export function TrialLink({ className = "site-button", secondary = false, children }: { className?: string; secondary?: boolean; children?: ReactNode }) {
  const { data, isPending } = usePublicConfig();
  const buyable = data?.signup_enabled && data.deployment_mode === "saas";
  return <Link className={secondary && buyable ? `${className} site-button-outline` : className} to="/signup">{children ?? (isPending ? "Explore your workspace" : data?.signup_enabled ? data.deployment_mode === "self_hosted" ? "Create your workspace" : `Start your ${data.trial_days}-day trial` : data?.deployment_mode === "self_hosted" ? "Workspace access" : "View launch details")}</Link>;
}

/** Hosted: the contact form. Self-hosted: the operator's published email, if any. */
export function ContactLink({ className, children = "Contact" }: { className?: string; children?: ReactNode }) {
  const { data } = usePublicConfig();
  if (data?.contact_form) return <Link className={className} to="/contact">{children}</Link>;
  if (data?.support_email) return <a className={className} href={`mailto:${data.support_email}`}>{children}</a>;
  return null;
}

/** Straight to checkout: signup first if needed, then billing with the seat count pre-filled.
 * Hidden where hosted signup isn't open (website preview, self-hosted). */
export function BuyLink({ seats = 1, className = "site-button" }: { seats?: number; className?: string }) {
  const { data } = usePublicConfig();
  const { isOwner } = useAuth();
  if (!data?.signup_enabled || data.deployment_mode !== "saas") return null;
  const href = isOwner ? `/billing?seats=${seats}#choose-seats` : `/signup?buy=${seats}`;
  return <a className={className} href={href} data-testid="buy-now">Buy now</a>;
}

export function SiteLayout({ children, title = "Trim Log | Your trim room, in view", description = "Track trim time, weights, harvests and crew progress in one shared workspace. Try managed hosting or run the open-source app on your own infrastructure." }: { children: ReactNode; title?: string; description?: string }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { isOwner, player } = useAuth();
  const { data } = usePublicConfig();
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute("content", description);
    document.body.classList.add("viewing-site");
    return () => document.body.classList.remove("viewing-site");
  }, [title, description]);

  return <div className="site-shell">
    <a className="site-skip" href="#main-content">Skip to content</a>
    <header className="site-header">
      <Brand />
      <button className="site-menu-toggle" type="button" aria-expanded={menuOpen} aria-controls="site-navigation" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? "Close menu" : "Menu"}<span aria-hidden="true">{menuOpen ? "×" : "☰"}</span></button>
      <nav id="site-navigation" className={`site-nav ${menuOpen ? "is-open" : ""}`} aria-label="Main navigation">
        <Link to="/tour" onClick={() => setMenuOpen(false)} activeProps={{ "aria-current": "page" }}>Product tour</Link>
        <Link to="/pricing" onClick={() => setMenuOpen(false)} activeProps={{ "aria-current": "page" }}>Pricing</Link>
        {isOwner || player ? <Link to={isOwner ? "/console" : "/"} className="site-button site-button-small">Open workspace</Link> : <><Link to="/login" onClick={() => setMenuOpen(false)}>Sign in</Link><TrialLink className="site-button site-button-small" secondary /><BuyLink className="site-button site-button-small" /></>}
      </nav>
    </header>
    {children}
    <footer className="site-footer">
      <div><Brand /><p>Trim tracking for hands-on crews.</p></div>
      <nav aria-label="Footer"><Link to="/tour">Product tour</Link><Link to="/pricing">Pricing</Link><Link to="/trim-pay-calculator">Trim pay calculator</Link><SourceLink /><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link><ContactLink /></nav>
      <p className="site-footer-note">{data?.signup_enabled ? data.deployment_mode === "self_hosted" ? "Self-hosted. Free software, on your infrastructure." : data.operator_name ? `© ${data.operator_name}` : null : data?.deployment_mode === "self_hosted" ? "Self-hosted. New owner registration is closed." : "Website preview. New hosted workspaces are not open yet."}</p>
    </footer>
    <ChatWidget />
  </div>;
}
